import { memo, useMemo } from 'react';
import { GoogleMap } from 'modules/shared/features/google-map';
import { ICameraCCTV } from '@api/models';

type CameraPolygonProps = {
    camera: ICameraCCTV;
    isSelected: boolean;
    selectedAngle?: number;
    selectedRange?: number;
    selectedFOV?: number;
    onClick?: () => void;
};

const CameraPolygon = memo(
    (props: CameraPolygonProps) => {
        const { camera, isSelected, selectedAngle, selectedRange, selectedFOV, onClick } = props;

        const angle = isSelected && selectedAngle !== undefined ? selectedAngle : +camera.angle || 45;
        const range = isSelected && selectedRange !== undefined ? selectedRange : +camera.distance || 100;
        const fov = isSelected && selectedFOV !== undefined ? selectedFOV : +camera.fieldOfView || 90;

        const center = useMemo(
            () => ({ lat: +camera.location.latitude, lng: +camera.location.longitude }),
            [camera.location.latitude, camera.location.longitude]
        );

        return (
            <GoogleMap.Polygon
                center={center}
                angle={angle}
                range={range}
                fieldOfView={fov}
                fillColor={isSelected ? '#4285F4' : '#6EB85E'}
                strokeColor={isSelected ? '#1967D2' : '#6EB85F'}
                fillOpacity={isSelected ? 0.45 : 0.35}
                strokeOpacity={0.8}
                strokeWeight={isSelected ? 2 : 1.6}
                onClick={onClick}
                // if supported by your Polygon wrapper:
                // zIndex={isSelected ? 5 : 1}
            />
        );
    },
    (prev, next) => {
        // IMPORTANT: only re-render if:
        // - camera identity/geometry changed
        // - selection changed
        // - selected values changed (ONLY matters if selected)
        if (prev.camera._id !== next.camera._id) return false;

        const sameCameraStatic =
            prev.camera.location.latitude === next.camera.location.latitude &&
            prev.camera.location.longitude === next.camera.location.longitude &&
            prev.camera.angle === next.camera.angle &&
            prev.camera.distance === next.camera.distance &&
            prev.camera.fieldOfView === next.camera.fieldOfView;

        if (!sameCameraStatic) return false;

        if (prev.isSelected !== next.isSelected) return false;

        // Only compare selected props when this polygon is selected (or becomes selected)
        if (next.isSelected) {
            return (
                prev.selectedAngle === next.selectedAngle &&
                prev.selectedRange === next.selectedRange &&
                prev.selectedFOV === next.selectedFOV
            );
        }

        return true;
    }
);

export default CameraPolygon;
